import React, { useState, useEffect, useRef } from 'react';
import { UserPlus, Edit2, Shield, Check, X, Search, Filter, Key, User, AlertCircle, Power, ChevronLeft, ChevronRight, ChevronDown } from 'lucide-react';
import Pagination from '../../../components/common/Pagination';
import Button from '../../../components/common/Button';
import Input from '../../../components/common/Input';
import { keycloakAdminService } from '../../../services/keycloakAdminService';
import StaffMemberRow from './staff/StaffRow';
import { getAllCustomers } from '../../../services/api/customerApi';

// Normalize a raw phone input to +971XXXXXXXXX format
const normalizePhone = (raw) => {
    if (!raw) return '+971';
    let digits = raw.replace(/[^\d+]/g, '');
    // Strip leading + to work on digits only
    if (digits.startsWith('+')) digits = digits.slice(1);
    // Strip country code 971 prefix if present
    if (digits.startsWith('971')) digits = digits.slice(3);
    // Strip leading 0
    if (digits.startsWith('0')) digits = digits.slice(1);
    return '+971' + digits;
};

const validatePhone = (phone) => {
    // Must match +971 followed by exactly 9 digits
    return /^\+971\d{9}$/.test(phone);
};

const StaffView = ({ currentRole }) => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [phoneError, setPhoneError] = useState('');
    const [usernameError, setUsernameError] = useState('');
    const [firstNameError, setFirstNameError] = useState('');
    const [lastNameError, setLastNameError] = useState('');
    const isFetching = React.useRef(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [isReadOnly, setIsReadOnly] = useState(false);
    const [isResetModalOpen, setIsResetModalOpen] = useState(false);
    const [newPassword, setNewPassword] = useState('');
    const [resetSuccess, setResetSuccess] = useState(false);
    const [availableRoles, setAvailableRoles] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [showPageSizeDropdown, setShowPageSizeDropdown] = useState(false);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const [openDropdownId, setOpenDropdownId] = useState(null);
    const [transferModalData, setTransferModalData] = useState(null);

    const isSalesManager = currentRole === 'SALES_MANAGER';

    // Prevent duplicate API calls in StrictMode
    const isFetchingRolesRef = useRef(false);

    // Form state
    const [formData, setFormData] = useState({
        username: '',
        email: '',
        firstName: '',
        lastName: '',
        phone: '+971',
        employeeId: '',
        vehicleNumber: '',
        userType: isSalesManager ? 'STAFF' : 'OWNER',
        roles: isSalesManager ? ['SALES_PERSON'] : [] // Multiple role selection
    });

    useEffect(() => {
        fetchUsers();
        fetchRoles();
    }, []);

    const fetchRoles = async () => {
        // Guard against duplicate calls
        if (isFetchingRolesRef.current) {
            console.log('[StaffView] Already fetching roles, skipping duplicate call');
            return;
        }

        isFetchingRolesRef.current = true;
        console.log('[StaffView] Starting to fetch roles...');

        try {
            const roles = await keycloakAdminService.getRoles();
            console.log('[StaffView] Fetched roles:', roles);
            
            // If sales manager, they can only assign SALES_PERSON role
            if (isSalesManager) {
                setAvailableRoles(roles.filter(r => r.name === 'SALES_PERSON'));
            } else {
                setAvailableRoles(roles);
            }
        } catch (err) {
            console.error('[StaffView] Failed to fetch roles:', err);
        } finally {
            // Reset the flag after fetch completes
            isFetchingRolesRef.current = false;
        }
    };

    const fetchUsers = async () => {
        if (isFetching.current) {
            console.log('Fetch already in progress, skipping...');
            return;
        }

        console.log('Fetching users from backend...');
        isFetching.current = true;
        setLoading(true);
        setError(null);
        try {
            const params = {
                page: currentPage - 1,
                size: pageSize,
                search: searchTerm,
                role: isSalesManager ? 'SALES_PERSON' : undefined
            };
            const result = await keycloakAdminService.fetchUsers(params);
            console.log('Successfully fetched users:', result.content ? result.content.length : result.length);
            setUsers(result.content || result || []);
            setTotalElements(result.totalElements || 0);
            setTotalPages(result.totalPages || 0);
        } catch (err) {
            console.error('Staff fetch error details:', err);
            setError(err.response?.status === 403 ? 'Access Denied: Permissions required to view staff.' : 'Failed to load staff members.');
            setUsers([]);
            setTotalElements(0);
            setTotalPages(0);
        } finally {
            setLoading(false);
            isFetching.current = false;
        }
    };

    // Refetch when pagination or search changes
    useEffect(() => {
        const delay = setTimeout(() => {
            fetchUsers();
        }, 300);
        return () => clearTimeout(delay);
    }, [currentPage, pageSize, searchTerm]);

    const handleOpenModal = async (user = null, readOnly = false) => {
        if (user) {
            console.log('Opening edit modal for user:', user.username);
            setSelectedUser(user);
            setLoading(true);

            let userRoles = [];
            try {
                const roles = await keycloakAdminService.getUserRoles(user.id);
                console.log('Roles found for user:', roles.map(r => r.name));
                userRoles = roles.map(r => r.name);
            } catch (err) {
                console.error('Failed to fetch user roles', err);
            } finally {
                setLoading(false);
            }

            const existingPhone = user.phone && user.phone.trim() ? normalizePhone(user.phone) : '+971';

            // Derive userType from the actual roles assigned to this user:
            // TENANT_OWNER / TENANT_ADMIN → 'OWNER'; everything else → 'STAFF'
            const ownerRoles = ['TENANT_OWNER', 'TENANT_ADMIN'];
            const derivedUserType = isSalesManager
                ? 'STAFF'
                : userRoles.some(r => ownerRoles.includes(r)) ? 'OWNER' : 'STAFF';

            setFormData({
                username: user.username,
                email: user.email || '',
                firstName: user.firstName || '',
                lastName: user.lastName || '',
                phone: existingPhone,
                employeeId: user.employeeId || '',
                vehicleNumber: user.vehicleNumber || '',
                userType: derivedUserType,
                roles: userRoles
            });
            setPhoneError('');
            setUsernameError('');
            setFirstNameError('');
            setLastNameError('');
            setIsEditing(!readOnly);
            setIsReadOnly(readOnly);
        } else {
            setFormData({
                username: '',
                email: '',
                firstName: '',
                lastName: '',
                phone: '+971',
                employeeId: '',
                vehicleNumber: '',
                userType: isSalesManager ? 'STAFF' : 'OWNER',
                roles: isSalesManager ? ['SALES_PERSON'] : []
            });
            setPhoneError('');
            setUsernameError('');
            setFirstNameError('');
            setLastNameError('');
            setIsEditing(false);
            setIsReadOnly(false);
        }
        setIsModalOpen(true);
    };

    const validateUsername = (username) => {
        // Lowercase alphanumeric, hyphens, underscores, dots, and @. No spaces.
        return /^[a-z0-9.\-_@]+$/.test(username);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validate username (only if not editing)
        if (!isEditing) {
            if (!formData.username?.trim()) {
                setUsernameError('Username is required');
                return;
            }
            if (!validateUsername(formData.username)) {
                setUsernameError('Username can only contain lowercase letters, numbers, dots (.), hyphens (-), underscores (_), and @. No spaces allowed.');
                return;
            }
        }
        setUsernameError('');

        // Validate mandatory first and last names
        let hasError = false;
        if (!formData.firstName?.trim()) {
            setFirstNameError('First Name is required');
            hasError = true;
        } else {
            setFirstNameError('');
        }
        if (!formData.lastName?.trim()) {
            setLastNameError('Last Name (Second Name) is required');
            hasError = true;
        } else {
            setLastNameError('');
        }
        if (hasError) return;

        // Validate phone
        const normalizedPhone = normalizePhone(formData.phone);
        if (!validatePhone(normalizedPhone)) {
            setPhoneError('Enter a valid UAE mobile number (e.g. +971 50 123 4567)');
            return;
        }
        setPhoneError('');

        setLoading(true);
        try {
            if (isEditing) {
                // Update existing user
                await keycloakAdminService.updateUser(selectedUser.id, {
                    firstName: formData.firstName,
                    lastName: formData.lastName,
                    email: formData.email,
                    phone: normalizedPhone,
                    employeeId: formData.employeeId,
                    vehicleNumber: formData.vehicleNumber
                });

                // Update roles if changed
                const currentRoles = await keycloakAdminService.getUserRoles(selectedUser.id);
                const currentRoleNames = currentRoles.map(r => r.name);

                // Find roles to add and remove
                const rolesToAdd = formData.roles.filter(r => !currentRoleNames.includes(r));
                const rolesToRemove = currentRoleNames.filter(r => !formData.roles.includes(r) && availableRoles.some(ar => ar.name === r));

                if (rolesToRemove.length > 0) {
                    await keycloakAdminService.removeRole(selectedUser.id, rolesToRemove.map(name => ({ name })));
                }
                if (rolesToAdd.length > 0) {
                    await keycloakAdminService.assignRole(selectedUser.id, rolesToAdd.map(name => ({ name })));
                }
            } else {
                // Create new user with roles
                await keycloakAdminService.createUser({
                    username: formData.username,
                    email: formData.email,
                    firstName: formData.firstName,
                    lastName: formData.lastName,
                    phone: formData.phone,
                    userType: formData.userType,
                    roles: formData.roles,
                    credentials: [{
                        type: 'password',
                        value: 'Temporary123!',
                        temporary: false
                    }]
                });
            }

            setIsModalOpen(false);
            fetchUsers();
        } catch (error) {
            console.error('Operation failed:', error);
            if (error.response) {
                console.error('Backend error response:', error.response.data);
            }
            alert('Action failed: ' + (error.response?.data?.message || error.message || 'Unknown error'));
        } finally {
            setLoading(false);
        }
    };

    const handleResetPassword = async (e) => {
        e.preventDefault();
        if (!newPassword) return;
        setLoading(true);
        try {
            await keycloakAdminService.resetPassword(selectedUser.id, newPassword);
            setResetSuccess(true);
            setNewPassword('');
        } catch (error) {
            alert(error.message || 'Failed to reset password.');
        } finally {
            setLoading(false);
        }
    };

    const handleToggleUserEnabled = async (user) => {
        // If enabling, or if not a salesperson, toggle directly
        const isSalesPerson = (user.realmRoles || []).includes('SALES_PERSON');
        if (!user.enabled || !isSalesPerson) {
            setLoading(true);
            try {
                await keycloakAdminService.setUserEnabled(user.id, !user.enabled);
                fetchUsers();
            } catch (error) {
                console.error('Failed to toggle user status:', error);
                alert('Failed to update user status: ' + (error.response?.data?.message || error.message || 'Unknown error'));
            } finally {
                setLoading(false);
            }
            return;
        }

        // Toggling to deactivated: check if has assigned/delegated customers
        setLoading(true);
        try {
            const customerCheck = await getAllCustomers({
                salesPersonId: user.publicId || user.id,
                salesPersonRole: 'all',
                size: 1,
                status: 'ACTIVE'
            });
            const customerCount = customerCheck?.totalElements || 0;

            if (customerCount > 0) {
                // Fetch other active salespeople
                const allSalesPersons = await keycloakAdminService.fetchUsers({ role: 'SALES_PERSON', size: 1000 });
                const activeOthers = (allSalesPersons.content || allSalesPersons || []).filter(u => u.enabled && u.id !== user.id);
                
                setTransferModalData({
                    user,
                    customerCount,
                    activeSalespeople: activeOthers,
                    selectedTargetId: ''
                });
            } else {
                // No customers, deactivate directly
                await keycloakAdminService.setUserEnabled(user.id, false);
                fetchUsers();
            }
        } catch (error) {
            console.error('Failed to check user customer assignments:', error);
            alert('Failed to check customer assignments: ' + (error.message || 'Unknown error'));
        } finally {
            setLoading(false);
        }
    };

    const handleConfirmDeactivateWithTransfer = async (transfer) => {
        if (!transferModalData) return;
        const { user, selectedTargetId } = transferModalData;
        setLoading(true);
        try {
            if (transfer && selectedTargetId) {
                // 1. Call transfer endpoint
                await keycloakAdminService.transferUserCustomers(user.id, selectedTargetId);
            }
            // 2. Disable user
            await keycloakAdminService.setUserEnabled(user.id, false);
            setTransferModalData(null);
            fetchUsers();
        } catch (error) {
            console.error('Failed to deactivate/transfer:', error);
            alert('Failed to process request: ' + (error.response?.data?.message || error.message || 'Unknown error'));
        } finally {
            setLoading(false);
        }
    };

    // Filter handled server-side now
    const filteredUsers = users;

    const paginatedUsers = users;

    // Reset to page 1 when search changes
    const handleSearch = (val) => {
        setSearchTerm(val);
        setCurrentPage(1);
    };

    return (
        <div className="flex flex-col h-screen overflow-hidden bg-gradient-to-br from-blue-50 via-white to-purple-50 p-2 lg:p-2.5">
            <div className="mb-2 md:mb-3">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-2">
                    <div>
                        <h1 className="text-xl md:text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                            {isSalesManager ? 'Sales Staff Management' : 'Staff Management'}
                        </h1>
                        <p className="text-gray-500 text-[10px] mt-0.5">
                            {isSalesManager ? 'Manage your sales team members' : 'Manage portal users and their access roles'}
                        </p>
                    </div>
                    <Button
                        onClick={() => handleOpenModal()}
                        className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl shadow-lg shadow-blue-200"
                    >
                        <UserPlus size={18} />
                        Add New Staff
                    </Button>
                </div>
            </div>

            <div className="flex-1 min-h-0 bg-white rounded-xl md:rounded-2xl shadow-sm border border-gray-100 overflow-hidden flex flex-col mb-1 lg:mb-0">
                <div className="p-2.5 lg:p-3 border-b border-gray-100 flex flex-col md:flex-row gap-3">
                    <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                        <input
                            type="text"
                            placeholder="Search staff by name or email..."
                            className="w-full pl-10 pr-4 py-1.5 lg:py-2 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all text-xs lg:text-sm"
                            value={searchTerm}
                            onChange={(e) => handleSearch(e.target.value)}
                        />
                    </div>
                    <div className="flex gap-2">
                        <button className="p-1.5 lg:p-2 bg-gray-50 border border-gray-200 rounded-xl text-gray-600 hover:bg-gray-100 transition-all">
                            <Filter size={16} />
                        </button>
                    </div>
                </div>
                <div className="flex-1 min-h-0 overflow-auto custom-scrollbar pb-20 md:pb-0">
                    {/* Table with unified rows for all screen sizes */}
                    <table className="w-full text-left">
                        <thead className="bg-gray-50 text-gray-400 text-[10px] font-black uppercase tracking-[0.2em] sticky top-0 z-10 shadow-sm border-b border-gray-100">
                            <tr>
                                <th className="px-3 py-3 w-[30%]">Staff Member</th>
                                <th className="px-3 py-3 w-[25%]">Contact Details</th>
                                <th className="px-3 py-3 w-[25%]">Access Roles</th>
                                <th className="px-3 py-3 text-right w-[20%] pr-4">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50 md:divide-y-0">
                            {loading ? (
                                Array(5).fill(0).map((_, i) => (
                                    <tr key={i} className="animate-pulse">
                                        <td className="px-4 py-6"><div className="h-10 bg-gray-100 rounded-xl w-40"></div></td>
                                        <td className="px-4 py-6"><div className="h-8 bg-gray-50 rounded-lg w-48"></div></td>
                                        <td className="px-4 py-6"><div className="h-8 bg-gray-50 rounded-lg w-24"></div></td>
                                        <td className="px-4 py-6"><div className="h-6 bg-gray-100 rounded-full w-20"></div></td>
                                        <td colSpan="2"></td>
                                    </tr>
                                ))
                            ) : error ? (
                                <tr>
                                    <td colSpan="6" className="px-6 py-24 text-center">
                                        <div className="flex flex-col items-center gap-4 text-red-500">
                                            <div className="p-4 bg-red-50 rounded-full">
                                                <Shield size={48} className="opacity-50" />
                                            </div>
                                            <div className="space-y-1">
                                                <p className="font-black uppercase tracking-widest text-sm">{error}</p>
                                                <p className="text-xs text-gray-400 max-w-xs mx-auto">Verify your administrative permissions or check your connection.</p>
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                            ) : filteredUsers.length > 0 ? (
                                paginatedUsers.map(user => (
                                    <StaffMemberRow
                                        key={user.id}
                                        user={user}
                                        onEdit={(u) => handleOpenModal(u, false)}
                                        onView={(u) => handleOpenModal(u, true)}
                                        onResetPassword={(u) => {
                                            setSelectedUser(u);
                                            setResetSuccess(false);
                                            setNewPassword('');
                                            setIsResetModalOpen(true);
                                        }}
                                        onToggleEnabled={handleToggleUserEnabled}
                                    />
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="6" className="px-6 py-24 text-center">
                                        <div className="flex flex-col items-center gap-3 text-gray-300">
                                            <User size={48} className="opacity-20" />
                                            <p className="font-black uppercase tracking-widest text-xs">No personnel found</p>
                                            <button 
                                                onClick={() => handleSearch('')}
                                                className="text-blue-500 text-[10px] font-bold underline uppercase tracking-widest"
                                            >
                                                Clear Search Filters
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination bar */}
                {!loading && !error && filteredUsers.length > 0 && (
                    <div className="border-t border-gray-100 px-2 py-1.5 bg-gray-50 flex items-center justify-between flex-shrink-0">
                        {/* Items per page selector */}
                        <div className="flex items-center gap-2">
                            <span className="text-[10px] text-gray-600">Show:</span>
                            <div className="relative">
                                <button
                                    onClick={() => setShowPageSizeDropdown(s => !s)}
                                    className="flex items-center gap-1 px-2 py-1 text-[10px] bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-all font-medium min-w-[50px]"
                                >
                                    {pageSize}
                                    <ChevronDown size={12} className={`transition-transform ${showPageSizeDropdown ? 'rotate-180' : ''}`} />
                                </button>
                                {showPageSizeDropdown && (
                                    <div className="absolute bottom-full mb-1 left-0 w-24 bg-white border border-gray-200 rounded-lg shadow-lg z-20 overflow-hidden">
                                        {[10, 25, 50].map(n => (
                                            <button
                                                key={n}
                                                onClick={() => { setPageSize(n); setCurrentPage(1); setShowPageSizeDropdown(false); }}
                                                className={`w-full text-center px-2 py-1 text-[10px] transition-colors ${pageSize === n ? 'bg-blue-50 text-blue-600 font-semibold' : 'text-gray-700 hover:bg-gray-50'
                                                    }`}
                                            >
                                                {n}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <span className="text-[10px] text-gray-600">of {totalElements} items</span>
                        </div>

                        {/* Page navigation */}
                        <div className="flex items-center gap-1">
                            <Pagination 
                                currentPage={currentPage}
                                totalPages={totalPages}
                                onPageChange={setCurrentPage}
                            />
                        </div>
                    </div>
                )}
            </div>

            {isModalOpen && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[200] md:p-4 animate-fade-in">
                    <div className="bg-white md:rounded-xl shadow-2xl w-full max-w-5xl h-[100dvh] md:h-auto md:max-h-[90vh] flex flex-col relative z-[201]">
                        {/* Header - Simple White */}
                        <div className="bg-white border-b border-gray-200 px-5 py-2.5 relative flex-shrink-0 rounded-t-xl">
                            <button
                                onClick={() => setIsModalOpen(false)}
                                className="absolute top-2.5 right-3 p-1 hover:bg-gray-100 rounded-full transition-all"
                                disabled={loading}
                            >
                                <X size={16} className="text-gray-600" />
                            </button>
                            <h2 className="text-base font-bold text-gray-900 pr-8">
                                {isReadOnly ? 'View Staff Member' : isEditing ? 'Edit Staff Member' : 'Register New Staff'}
                            </h2>
                            <p className="text-gray-500 text-xs">
                                {isReadOnly ? 'Staff member details' : isEditing ? 'Update staff information and roles' : 'Add a new team member to your organization'}
                            </p>
                        </div>

                        {/* Form Content */}
                        <div className="flex-1 overflow-y-auto min-h-0">
                            <form onSubmit={handleSubmit} className="p-4">
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                    {/* Left Column - Personal Information */}
                                    <div>
                                        <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                                            <h3 className="text-xs font-semibold text-gray-800 mb-2 flex items-center gap-2">
                                                <User size={14} className="text-blue-600" />
                                                Personal Information
                                            </h3>

                                            <div className="space-y-2">
                                                {!isEditing && (
                                                    <div>
                                                        <label className="block text-xs font-medium text-gray-700 mb-0.5">
                                                            Username <span className="text-red-500">*</span>
                                                        </label>
                                                        <input
                                                            type="text"
                                                            value={formData.username}
                                                            onChange={(e) => {
                                                                setFormData({ ...formData, username: e.target.value.toLowerCase().trim() });
                                                                if (usernameError) setUsernameError('');
                                                            }}
                                                            className={`w-full px-2.5 py-1.5 text-xs border rounded-lg focus:outline-none focus:ring-1 ${usernameError
                                                                ? 'border-red-400 focus:ring-red-400 focus:border-red-400'
                                                                : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                                                                } disabled:bg-gray-50 disabled:text-gray-500`}
                                                            placeholder="john_doe"
                                                            required
                                                            disabled={isReadOnly}
                                                        />
                                                        {usernameError && (
                                                            <p className="mt-0.5 text-xs text-red-500">{usernameError}</p>
                                                        )}
                                                    </div>
                                                )}

                                                <div className="grid grid-cols-2 gap-2">
                                                    <div>
                                                        <label className="block text-xs font-medium text-gray-700 mb-0.5">
                                                            First Name <span className="text-red-500">*</span>
                                                        </label>
                                                        <input
                                                            type="text"
                                                            value={formData.firstName}
                                                            onChange={(e) => {
                                                                setFormData({ ...formData, firstName: e.target.value });
                                                                if (firstNameError) setFirstNameError('');
                                                            }}
                                                            className={`w-full px-2.5 py-1.5 text-xs border rounded-lg focus:outline-none focus:ring-1 ${firstNameError
                                                                ? 'border-red-400 focus:ring-red-400 focus:border-red-400'
                                                                : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                                                                } disabled:bg-gray-50 disabled:text-gray-500`}
                                                            placeholder="John"
                                                            required
                                                            disabled={isReadOnly}
                                                        />
                                                        {firstNameError && (
                                                            <p className="mt-0.5 text-xs text-red-500">{firstNameError}</p>
                                                        )}
                                                    </div>
                                                    <div>
                                                        <label className="block text-xs font-medium text-gray-700 mb-0.5">
                                                            Last Name <span className="text-red-500">*</span>
                                                        </label>
                                                        <input
                                                            type="text"
                                                            value={formData.lastName}
                                                            onChange={(e) => {
                                                                setFormData({ ...formData, lastName: e.target.value });
                                                                if (lastNameError) setLastNameError('');
                                                            }}
                                                            className={`w-full px-2.5 py-1.5 text-xs border rounded-lg focus:outline-none focus:ring-1 ${lastNameError
                                                                ? 'border-red-400 focus:ring-red-400 focus:border-red-400'
                                                                : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                                                                } disabled:bg-gray-50 disabled:text-gray-500`}
                                                            placeholder="Doe"
                                                            required
                                                            disabled={isReadOnly}
                                                        />
                                                        {lastNameError && (
                                                            <p className="mt-0.5 text-xs text-red-500">{lastNameError}</p>
                                                        )}
                                                    </div>
                                                </div>

                                                <div>
                                                    <label className="block text-xs font-medium text-gray-700 mb-0.5">
                                                        Email Address <span className="text-red-500">*</span>
                                                    </label>
                                                    <input
                                                        type="email"
                                                        value={formData.email}
                                                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                                        className="w-full px-2.5 py-1.5 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:text-gray-500"
                                                        placeholder="john@yaseero.com"
                                                        required
                                                        disabled={isReadOnly}
                                                    />
                                                </div>

                                                <div>
                                                    <label className="block text-xs font-medium text-gray-700 mb-0.5">
                                                        Phone Number <span className="text-red-500">*</span>
                                                    </label>
                                                    <input
                                                        type="tel"
                                                        value={formData.phone}
                                                        onChange={(e) => {
                                                            setFormData({ ...formData, phone: e.target.value });
                                                            if (phoneError) setPhoneError('');
                                                        }}
                                                        onBlur={(e) => {
                                                            const norm = normalizePhone(e.target.value);
                                                            setFormData(prev => ({ ...prev, phone: norm }));
                                                            if (!validatePhone(norm)) {
                                                                setPhoneError('Enter a valid UAE mobile number (e.g. +971501234567)');
                                                            } else {
                                                                setPhoneError('');
                                                            }
                                                        }}
                                                        className={`w-full px-2.5 py-1.5 text-xs border rounded-lg focus:outline-none focus:ring-1 ${phoneError
                                                            ? 'border-red-400 focus:ring-red-400 focus:border-red-400'
                                                            : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                                                            } disabled:bg-gray-50 disabled:text-gray-500`}
                                                        placeholder="+971 50 123 4567"
                                                        required
                                                        disabled={isReadOnly}
                                                    />
                                                    {phoneError && (
                                                        <p className="mt-0.5 text-xs text-red-500">{phoneError}</p>
                                                    )}
                                                </div>

                                                <div>
                                                    <label className="block text-xs font-medium text-gray-700 mb-0.5">
                                                        Employee ID
                                                    </label>
                                                    <input
                                                        type="text"
                                                        value={formData.employeeId}
                                                        onChange={(e) => setFormData({ ...formData, employeeId: e.target.value })}
                                                        className="w-full px-2.5 py-1.5 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:text-gray-500"
                                                        placeholder="EMP001"
                                                        disabled={isReadOnly}
                                                    />
                                                </div>

                                                <div>
                                                    <label className="block text-xs font-medium text-gray-700 mb-0.5">
                                                        Vehicle Number
                                                    </label>
                                                    <input
                                                        type="text"
                                                        value={formData.vehicleNumber}
                                                        onChange={(e) => setFormData({ ...formData, vehicleNumber: e.target.value })}
                                                        className="w-full px-2.5 py-1.5 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:text-gray-500"
                                                        placeholder="V-12345"
                                                        disabled={isReadOnly}
                                                    />
                                                </div>

                                                {!isEditing && (
                                                    <div>
                                                        <label className="block text-xs font-medium text-gray-700 mb-0.5">
                                                            User Type <span className="text-red-500">*</span>
                                                        </label>
                                                        <select
                                                            value={formData.userType}
                                                            onChange={(e) => setFormData({ ...formData, userType: e.target.value })}
                                                            className="w-full px-2.5 py-1.5 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
                                                            disabled={isSalesManager || isReadOnly}
                                                        >
                                                            {!isSalesManager && (
                                                                <>
                                                                    <option value="OWNER">🏢 Owner - Supplier</option>
                                                                </>
                                                            )}
                                                            <option value="STAFF">👔 Staff - Supervisor & Sales Management</option>
                                                        </select>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Right Column - Role Assignment */}
                                    <div>
                                        <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                                            <h3 className="text-xs font-semibold text-gray-800 mb-2 flex items-center gap-2">
                                                <Shield size={14} className="text-purple-600" />
                                                Role Assignment
                                            </h3>

                                            {(() => {
                                                const visibleRoles = availableRoles.filter(role => role.name !== 'TENANT_OWNER');
                                                const visibleRoleNames = visibleRoles.map(r => r.name);
                                                const selectedVisibleRolesCount = formData.roles.filter(name => visibleRoleNames.includes(name)).length;

                                                return (
                                                    <>
                                                        <div className="space-y-1.5 max-h-[340px] overflow-y-auto pr-1">
                                                            {availableRoles.length > 0 ? (
                                                                visibleRoles.map(role => (
                                                                    <label
                                                                        key={role.name}
                                                                        className={`
                                                                        flex items-start gap-2 p-2 rounded-lg cursor-pointer transition-all duration-200
                                                                        ${formData.roles.includes(role.name)
                                                                                ? 'bg-blue-500 text-white shadow-sm'
                                                                                : 'bg-white hover:bg-gray-100 border border-gray-200'
                                                                            }
                                                                    `}
                                                                    >
                                                                        <input
                                                                            type="checkbox"
                                                                            checked={formData.roles.includes(role.name)}
                                                                            onChange={(e) => {
                                                                                if (e.target.checked) {
                                                                                    setFormData({ ...formData, roles: [...formData.roles, role.name] });
                                                                                } else {
                                                                                    setFormData({ ...formData, roles: formData.roles.filter(r => r !== role.name) });
                                                                                }
                                                                            }}
                                                                            className="mt-0.5 w-3.5 h-3.5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 disabled:opacity-50"
                                                                            disabled={isReadOnly}
                                                                        />
                                                                        <div className="flex-1 min-w-0">
                                                                            <div className={`font-semibold text-xs ${formData.roles.includes(role.name) ? 'text-white' : 'text-gray-900'}`}>
                                                                                {role.name.replace(/_/g, ' ')}
                                                                            </div>
                                                                            {role.description && (
                                                                                <div className={`text-xs mt-0.5 line-clamp-1 ${formData.roles.includes(role.name) ? 'text-blue-100' : 'text-gray-500'}`}>
                                                                                    {role.description}
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                        {formData.roles.includes(role.name) && (
                                                                            <Check size={14} className="text-white flex-shrink-0" />
                                                                        )}
                                                                    </label>
                                                                ))
                                                            ) : (
                                                                <div className="text-center py-4">
                                                                    <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-1.5"></div>
                                                                    <p className="text-xs text-gray-500">Loading roles...</p>
                                                                </div>
                                                            )}
                                                        </div>

                                                        {selectedVisibleRolesCount === 0 && availableRoles.length > 0 && (
                                                            <div className="mt-2 p-1.5 bg-red-50 border border-red-200 rounded-lg">
                                                                <p className="text-xs text-red-600 font-medium flex items-center gap-1">
                                                                    <AlertCircle size={11} />
                                                                    Select at least one role
                                                                </p>
                                                            </div>
                                                        )}

                                                        {selectedVisibleRolesCount > 0 && (
                                                            <div className="mt-2 p-1.5 bg-green-50 border border-green-200 rounded-lg">
                                                                <p className="text-xs text-green-700 font-medium flex items-center gap-1">
                                                                    <Check size={11} />
                                                                    {selectedVisibleRolesCount} role{selectedVisibleRolesCount > 1 ? 's' : ''} selected
                                                                </p>
                                                            </div>
                                                        )}
                                                    </>
                                                );
                                            })()}

                                            {!isEditing && (
                                                <div className="mt-2 p-1.5 bg-blue-50 border border-blue-200 rounded-lg">
                                                    <p className="text-xs text-blue-800 flex items-center gap-1">
                                                        <Key size={11} />
                                                        <span>
                                                            Default password: <span className="font-mono font-semibold">Temporary123!</span>
                                                        </span>
                                                    </p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </form>
                        </div>

                        {/* Footer */}
                        <div className="border-t border-gray-200 px-4 py-2 bg-gray-50 flex gap-2 justify-end flex-shrink-0 rounded-b-xl">
                            <button
                                type="button"
                                onClick={() => setIsModalOpen(false)}
                                disabled={loading}
                                className="px-3 py-1.5 text-xs border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-all font-semibold disabled:opacity-50"
                            >
                                Cancel
                            </button>
                            {!isReadOnly && (
                            <button
                                onClick={handleSubmit}
                                disabled={formData.roles.length === 0 || loading}
                                className="px-3 py-1.5 text-xs bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all font-semibold shadow-sm hover:shadow disabled:opacity-50 flex items-center gap-1.5"
                            >
                                {loading ? (
                                    <>
                                        <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                        Processing...
                                    </>
                                ) : (
                                    <>
                                        {isEditing ? (
                                            <>
                                                <Check size={13} />
                                                Save Changes
                                            </>
                                        ) : (
                                            <>
                                                <UserPlus size={13} />
                                                Add Staff
                                            </>
                                        )}
                                    </>
                                )}
                            </button>
                            )}
                        </div>
                    </div>
                </div>
            )}
            {/* Reset Password Modal */}
            {isResetModalOpen && (
                <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm animate-fade-in">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-slide-up relative z-[201]">
                        <div className="p-5 border-b border-gray-100 flex justify-between items-center">
                            <div>
                                <h2 className="text-base font-bold text-gray-900">Reset Password</h2>
                                <p className="text-xs text-gray-500 mt-0.5">for <span className="font-semibold text-gray-700">{selectedUser?.username}</span></p>
                            </div>
                            <button onClick={() => { setIsResetModalOpen(false); setResetSuccess(false); setNewPassword(''); }} className="text-gray-400 hover:text-gray-600">
                                <X size={20} />
                            </button>
                        </div>
                        {resetSuccess ? (
                            <div className="p-8 flex flex-col items-center gap-3 text-center">
                                <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center">
                                    <Check size={28} className="text-green-600" />
                                </div>
                                <p className="text-sm font-semibold text-gray-800">Password reset successfully!</p>
                                <p className="text-xs text-gray-500">The new password is now active for {selectedUser?.username}.</p>
                                <Button
                                    onClick={() => { setIsResetModalOpen(false); setResetSuccess(false); setNewPassword(''); }}
                                    className="mt-2 bg-green-600 hover:bg-green-700 text-white rounded-xl"
                                >
                                    Done
                                </Button>
                            </div>
                        ) : (
                            <form onSubmit={handleResetPassword} className="p-5 space-y-4">
                                <Input
                                    label="New Password"
                                    type="password"
                                    placeholder="Min. 8 characters"
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    required
                                    minLength={8}
                                />
                                <div className="flex gap-3">
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={() => setIsResetModalOpen(false)}
                                        className="flex-1 rounded-xl"
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        type="submit"
                                        className="flex-1 bg-orange-600 hover:bg-orange-700 text-white rounded-xl shadow-lg border-none"
                                        disabled={loading || newPassword.length < 8}
                                    >
                                        {loading ? 'Resetting...' : 'Reset Password'}
                                    </Button>
                                </div>
                            </form>
                        )}
                    </div>
                </div>
            )}
            {/* Transfer Customers Modal */}
            {transferModalData && (
                <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden relative z-[201] p-6 space-y-4">
                        <div className="flex items-start gap-3">
                            <div className="p-2 bg-amber-50 text-amber-600 rounded-xl shrink-0">
                                <AlertCircle size={24} />
                            </div>
                            <div className="space-y-1">
                                <h3 className="text-base font-bold text-gray-900">Transfer Customer Assignments</h3>
                                <p className="text-xs text-gray-500">
                                    <span className="font-semibold text-gray-700">
                                        {transferModalData.user.firstName || transferModalData.user.username}
                                    </span> is currently associated with <span className="font-bold text-blue-600">{transferModalData.customerCount}</span> customer(s).
                                </p>
                            </div>
                        </div>

                        <div className="space-y-2">
                            <label className="text-[11px] font-black text-gray-400 uppercase tracking-wider">Target Salesperson</label>
                            <select
                                value={transferModalData.selectedTargetId}
                                onChange={(e) => setTransferModalData({ ...transferModalData, selectedTargetId: e.target.value })}
                                className="w-full px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all"
                            >
                                <option value="">-- Select Active Salesperson --</option>
                                {transferModalData.activeSalespeople.map(u => (
                                    <option key={u.id} value={u.id}>
                                        {u.firstName || u.username} {u.lastName || ''} ({u.email || 'No email'})
                                    </option>
                                ))}
                            </select>
                            <p className="text-[10px] text-gray-400 italic">Select a salesperson to transfer all assigned and delegated accounts to them automatically.</p>
                        </div>

                        <div className="flex flex-col gap-2 pt-2 text-center">
                            <button
                                onClick={() => handleConfirmDeactivateWithTransfer(true)}
                                disabled={!transferModalData.selectedTargetId || loading}
                                className="w-full py-2.5 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 disabled:opacity-50 text-xs shadow-md transition-all flex items-center justify-center gap-1.5"
                            >
                                {loading ? 'Processing...' : 'Transfer & Deactivate'}
                            </button>
                            <button
                                onClick={() => handleConfirmDeactivateWithTransfer(false)}
                                disabled={loading}
                                className="w-full py-2.5 bg-red-50 text-red-600 font-semibold rounded-xl hover:bg-red-100 text-xs transition-all"
                            >
                                Deactivate Only (Keep Unassigned)
                            </button>
                            <button
                                onClick={() => setTransferModalData(null)}
                                disabled={loading}
                                className="w-full py-2.5 border text-gray-500 font-semibold rounded-xl hover:bg-gray-50 text-xs transition-all"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default StaffView;
